import {
  Category,
  Diamond,
  ElectricBolt,
  LocalFireDepartment,
  LocalGasStation,
  Thermostat,
  Water,
} from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { ReactElement } from 'react';
import type { CollectionPointType } from './collectionPointUtils';

const normalizeCommodityText = (value: string): string =>
  value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const energyTypeColorMap: Record<CollectionPointType, string> = {
  electricity: '#5e35b1',
  gas: '#fbc02d',
  water: '#0288d1',
  coal: '#546e7a',
  heat: '#ef6c00',
  other: '#2e7d32',
};

const energyTypeIconMap: Record<CollectionPointType, (props: SvgIconProps) => ReactElement> = {
  electricity: (props) => <ElectricBolt {...props} />,
  gas: (props) => <LocalFireDepartment {...props} />,
  water: (props) => <Water {...props} />,
  coal: (props) => <Diamond {...props} />,
  heat: (props) => <Thermostat {...props} />,
  other: (props) => <Category {...props} />,
};

export const getEnergyTypeIcon = (type: string, props: SvgIconProps = {}): ReactElement => {
  const renderIcon = energyTypeIconMap[type as CollectionPointType] ?? energyTypeIconMap.other;
  return renderIcon(props);
};

export const getEnergyTypeColor = (type: string): string =>
  energyTypeColorMap[type as CollectionPointType] ?? energyTypeColorMap.other;

export const getOtherCommoditySubtype = (label: string, key: string): 'fuel' | 'biomass' | 'wood' | null => {
  const normalized = normalizeCommodityText(`${key} ${label}`);
  if (normalized.includes('benzin') || normalized.includes('nafta') || normalized.includes('diesel') || normalized.includes('petrol')) return 'fuel';
  if (normalized.includes('drevo')) return 'wood';
  if (normalized.includes('pelet') || normalized.includes('pelety') || normalized.includes('biomas')) return 'biomass';
  return null;
};

export const getEnergyCommodityIcon = (type: string, label: string, key: string, props: SvgIconProps = {}): ReactElement => {
  if (type !== 'other') {
    return getEnergyTypeIcon(type, props);
  }

  const otherSubtype = getOtherCommoditySubtype(label, key);
  if (otherSubtype === 'fuel') return <LocalGasStation {...props} />;
  if (otherSubtype === 'biomass' || otherSubtype === 'wood') return <LocalFireDepartment {...props} />;
  return getEnergyTypeIcon('other', props);
};

export const getEnergyCommodityColor = (type: string, label: string, key: string): string => {
  if (type !== 'other') {
    return getEnergyTypeColor(type);
  }

  const otherSubtype = getOtherCommoditySubtype(label, key);
  if (otherSubtype === 'fuel') return '#00897b';
  if (otherSubtype === 'biomass') return '#6d4c41';
  if (otherSubtype === 'wood') return '#757575';
  return getEnergyTypeColor('other');
};
